import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useCampaign } from '../context/CampaignContext';
import { t } from '../lib/i18n';
import { MainLayout } from '../components/layout/MainLayout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { formatDate, formatRelativeTime, getRoleDisplay } from '../lib/utils';
import { 
  Plus, 
  Scroll, 
  Calendar, 
  Users, 
  Compass,
  ChevronRight,
  Sparkles
} from 'lucide-react';

export default function DashboardPage() {
  const { user, language } = useAuth();
  const { campaigns, fetchCampaigns, loading } = useCampaign();
  const navigate = useNavigate();

  useEffect(() => {
    fetchCampaigns().catch(console.error);
  }, [fetchCampaigns]);

  return (
    <MainLayout showSidebar={false}>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard-page">
        {/* Welcome Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="font-cinzel text-3xl font-bold">
              {t('dashboard_welcome', language)}, {user?.username}
            </h1>
            <p className="text-muted-foreground mt-1">
              {language === 'fr' 
                ? 'Gérez vos campagnes et préparez vos sessions'
                : 'Manage your campaigns and prepare your sessions'}
            </p>
          </div>
          <Button 
            onClick={() => navigate('/campaigns')} 
            className="fantasy-button"
            data-testid="view-campaigns-btn"
          >
            <Scroll className="mr-2 h-4 w-4" />
            {t('nav_campaigns', language)}
          </Button>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('campaigns', language)}</p>
                  <p className="text-3xl font-bold font-cinzel">{campaigns.length}</p>
                </div>
                <div className="p-3 rounded-full bg-primary/10">
                  <Scroll className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'En tant que MJ' : 'As GM'}
                  </p>
                  <p className="text-3xl font-bold font-cinzel">
                    {campaigns.filter(c => c.members.find(m => m.user_id === user?.id)?.role === 'owner').length}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-accent/10">
                  <Sparkles className="h-6 w-6 text-accent" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">
                    {language === 'fr' ? 'En tant que joueur' : 'As Player'}
                  </p>
                  <p className="text-3xl font-bold font-cinzel">
                    {campaigns.filter(c => c.members.find(m => m.user_id === user?.id)?.role === 'member').length}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-success/10">
                  <Users className="h-6 w-6 text-success" />
                </div>
              </div>
            </CardContent>
          </Card>
          
          <Card className="glass-card card-lift cursor-pointer" onClick={() => navigate('/campaigns')}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{t('campaign_create', language)}</p>
                  <p className="text-lg font-medium text-primary">
                    {language === 'fr' ? 'Nouvelle aventure' : 'New adventure'}
                  </p>
                </div>
                <div className="p-3 rounded-full bg-primary/10">
                  <Plus className="h-6 w-6 text-primary" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Campaigns List */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-cinzel text-xl font-bold">{t('campaigns', language)}</h2>
            <Button variant="ghost" onClick={() => navigate('/campaigns')} data-testid="view-all-campaigns">
              {t('view_all', language)}
              <ChevronRight className="ml-1 h-4 w-4" />
            </Button>
          </div>

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <Card key={i} className="glass-card">
                  <CardContent className="p-6">
                    <div className="animate-pulse space-y-4">
                      <div className="h-6 bg-muted rounded w-3/4" />
                      <div className="h-4 bg-muted rounded w-1/2" />
                      <div className="h-4 bg-muted rounded w-full" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : campaigns.length === 0 ? (
            <Card className="glass-card">
              <CardContent className="py-12 text-center">
                <Scroll className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-cinzel text-lg font-bold mb-2">{t('dashboard_no_campaigns', language)}</h3>
                <p className="text-muted-foreground mb-6">{t('dashboard_create_first', language)}</p>
                <Button onClick={() => navigate('/campaigns')} className="fantasy-button" data-testid="create-first-campaign">
                  <Plus className="mr-2 h-4 w-4" />
                  {t('campaign_create', language)}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {campaigns.slice(0, 6).map((campaign) => {
                const userMember = campaign.members.find(m => m.user_id === user?.id);
                return (
                  <Card 
                    key={campaign.id} 
                    className="glass-card card-interactive card-lift"
                    onClick={() => navigate(`/campaigns/${campaign.id}`)}
                    data-testid={`campaign-card-${campaign.id}`}
                  >
                    {campaign.banner_url && (
                      <div 
                        className="h-32 bg-cover bg-center rounded-t-lg"
                        style={{ backgroundImage: `url(${campaign.banner_url})` }}
                      />
                    )}
                    <CardHeader className={campaign.banner_url ? 'pt-4' : ''}>
                      <div className="flex items-start justify-between">
                        <div>
                          <CardTitle className="font-cinzel">{campaign.name}</CardTitle>
                          <CardDescription className="mt-1">
                            {campaign.universe || 'D&D 5e'}
                          </CardDescription>
                        </div>
                        {userMember && (
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            userMember.role === 'owner' ? 'bg-primary/10 text-primary' : 'bg-secondary text-secondary-foreground'
                          }`}>
                            {getRoleDisplay(userMember.role, language)}
                          </span>
                        )}
                      </div>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {campaign.description || (language === 'fr' ? 'Aucune description' : 'No description')}
                      </p>
                      <div className="flex items-center justify-between text-xs text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Users className="h-3 w-3" />
                          <span>{campaign.members.length} {language === 'fr' ? 'membres' : 'members'}</span>
                        </div>
                        <span>{formatRelativeTime(campaign.created_at, language === 'fr' ? 'fr-FR' : 'en-US')}</span>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </MainLayout>
  );
}
